import { FC, useMemo, useState } from 'react';
import { debounce, Stack } from '@mui/material';
import CandidateSportFilter from '../../../../CandidatesPage/components/CandidatesSidebar/CandidatesFilters/CandidateSportFilter';
import { useSearchParams } from 'react-router-dom';
import { useGetPaginatedSportsQuery } from '../../../../../store/apiSlice/sportApi';
import FilterLayout from '../../../../../layouts/FilterLayout';
import CountryFilter from './CountryFilter';
import LeagueTeamFilter from './LeagueTeamFilter';
import FilterActionButtons from '../../../../CandidatesPage/components/CandidatesSidebar/CandidatesFilters/FilterActionButtons';
import {
  useGetLeaguesByMultipleSportsQuery,
  useGetTeamsByMultipleLeaguesQuery,
} from '../../../../../store/apiSlice/careerApi';
import { withUnderScore } from '../../../../../utils/withUnderScore';
import { ISport } from '../../../../../interfaces/sport.interface';

interface PeopleFiltersProps {}

const PeopleFilters: FC<PeopleFiltersProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [countryFilterState, setCountryFilterState] = useState<string>(
    searchParams.get('country') ?? ''
  );

  const [leagueFilterState, setLeagueFilterState] = useState<string>(
    searchParams.get('leagues') ?? ''
  );

  const [teamFilterState, setTeamFilterState] = useState<string>(
    searchParams.get('teams') ?? ''
  );

  const [selectedSports, setSelectedSports] = useState<ISport[]>([]);

  const [limit, setLimit] = useState<number>(5);
  const [searchSportName, setSearchSportName] = useState<string>('');
  const [resetTrigger, setResetTrigger] = useState(false);

  const {
    data: sportsPaginationResponse,
    isLoading,
    isError,
    error,
  } = useGetPaginatedSportsQuery({
    page: 1,
    limit,
    search: searchSportName,
    sort: 'asc',
  });

  const formattedSports = selectedSports
    ? selectedSports
        .map((sport) => sport.name)
        .map(withUnderScore)
        .join(',')
    : '';

  const {
    data: leagues = [],
    isLoading: _isLeagueLoading,
    isError: _isLeagueErrors,
  } = useGetLeaguesByMultipleSportsQuery(formattedSports, {
    skip: !selectedSports.length,
  });

  const formattedLeagues = leagueFilterState
    ? leagueFilterState.split(',').map(withUnderScore).join(',')
    : '';

  const {
    data: teams = [],
    isLoading: _isTeamLoading,
    isError: _isTeamError,
  } = useGetTeamsByMultipleLeaguesQuery(formattedLeagues, {
    skip: !leagueFilterState,
  });

  const handleShowMore = () => {
    setLimit((prevLimit) => prevLimit + 5);
  };

  const handleFilterReset = () => {
    setCountryFilterState('');
    setLeagueFilterState('');
    setTeamFilterState('');
    setSearchSportName('');
    setSelectedSports([]);
    setResetTrigger((prev) => !prev);
    setLimit(5);

    setSearchParams((prevParams) => {
      prevParams.set('country', '');
      prevParams.set('sports', '');
      prevParams.set('leagues', '');
      prevParams.set('teams', '');
      prevParams.set('page', '1');

      return prevParams;
    });
  };

  const handleFilterApply = () => {
    setSearchParams((prevParams) => {
      prevParams.set('country', countryFilterState);
      prevParams.set('leagues', leagueFilterState);
      prevParams.set('teams', teamFilterState);
      prevParams.set('sports', formattedSports);
      prevParams.set('page', '1');

      return prevParams;
    });
  };

  const handleSearchSportNameChange = useMemo(
    () => debounce((value: string) => setSearchSportName(value), 300),
    []
  );

  const handleSelectedSport = (sport: ISport): void => {
    setSelectedSports((prev) =>
      prev.some((selected) => selected.id === sport.id)
        ? prev.filter((selected) => selected.id !== sport.id)
        : [...prev, sport]
    );
  };

  return (
    <Stack direction={'column'}>
      <FilterLayout name={'country'}>
        <CountryFilter
          countryFilterState={countryFilterState}
          setCountryFilterState={setCountryFilterState}
        />
      </FilterLayout>

      <CandidateSportFilter
        sports={sportsPaginationResponse?.data ?? []}
        isLoading={isLoading}
        isError={isError}
        totalSportsCount={sportsPaginationResponse?.total ?? 0}
        label={'sports'}
        error={error}
        onShowMore={handleShowMore}
        selectedSports={selectedSports}
        handleSelectedSport={handleSelectedSport}
        handleSearchChange={handleSearchSportNameChange}
        resetTrigger={resetTrigger}
      />

      <FilterLayout name={'League'}>
        <LeagueTeamFilter
          isActive={!!selectedSports.length}
          leagues={leagues ?? []}
          leagueFilterState={leagueFilterState}
          setLeagueFilterState={setLeagueFilterState}
        />
      </FilterLayout>

      <FilterLayout name={'Team'}>
        <LeagueTeamFilter
          isActive={!!leagueFilterState}
          teams={teams ?? []}
          teamFilterState={teamFilterState}
          setTeamFilterState={setTeamFilterState}
        />
      </FilterLayout>

      <FilterActionButtons
        handleFilterReset={handleFilterReset}
        handleFilterApply={handleFilterApply}
      />
    </Stack>
  );
};

export default PeopleFilters;
